import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Layers, MapPin, FileStack, List, ListTree, Map, Video, Image, Network, Maximize2, Minimize2, Database, Microscope } from 'lucide-react'
import { AnalyticsStatusIndicator } from '@/lib/utils/analytics-status'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useObject, useObjectVersions, useRolledUpRTO, useRolledUpRPO, useObjectRtoDetails, useServiceTable, useRunbook, useSaveRunbook, useScenarios, useObjectsInformation } from '../hooks'
import { ScenarioSelect } from '../components/ScenarioSelect'
import { RunbookTable } from '../components/RunbookTable'
import { ChangeRunbookDialog } from '../components/ChangeRunbookDialog'
import { CloneRunbookDialog } from '../components/CloneRunbookDialog'
import { SaveAsMasterDialog } from '../components/SaveAsMasterDialog'
import { RetrieveVersionDialog } from '../components/RetrieveVersionDialog'
import { RequestRecoveryDialog } from '../components/RequestRecoveryDialog'
import { StartForensicsDialog } from '@/features/forensics/components/StartForensicsDialog'
import { FloorPlanViewTab } from '../components/FloorPlanViewTab'
import { RecoveryDocumentVideoViewTab } from '../components/RecoveryDocumentVideoViewTab'
import { ObjectImageViewTab } from '../components/ObjectImageViewTab'
import { AnalyticsReportDialog } from '../components/AnalyticsReportDialog'
import { AnomalySizeChartDialog } from '../components/AnomalySizeChartDialog'
import { ReconciliationChart } from '@/components/charts/ReconciliationChart'
import { AnomalyHistoryChart } from '../components/AnomalyHistoryChart'
import type { RunbookTask } from '@/types/entities'
import { AdminRoute } from '@/components/auth/AdminRoute'
import { DataTable } from '@/components/data/DataTable'
import { createObjectVersionColumns } from '../columns-versions'
import { createServiceTableColumns } from '../columns-service'
import { formatSecondsToDHM } from '@/features/objects/utils/formatTime'
import { formatBytes } from '@/features/objects/utils/formatSize'
import { JsonViewDialog } from '@/components/ui/JsonViewDialog'
import { useMemo, useState, useCallback, useEffect, useRef, Suspense, lazy } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { objectsApi } from '../api'
import { ReactFlowProvider } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { buildIconPath } from '@/lib/imagePaths'
import { TranslatableText } from '@/components/ui/TranslatableText'

const VisualizationApp = lazy(() => import('../../visualization/_legacy/App'))
const AppStateProvider = lazy(() =>
    import('../../visualization/_legacy/context/AppStateContext').then((m) => ({
        default: m.AppStateProvider,
    }))
)
// Status indicator component (red/amber use expanding-ring pulse)
function StatusIndicator({ status }: { status: number | null }) {
    if (status === null || status === undefined) {
        return <span className="text-muted-foreground">-</span>
    }
    return <AnalyticsStatusIndicator status={status} size="h-4 w-4" showRing />
}
// Time Display Box component - styled rectangle for RTO/RPO values
function TimeDisplayBox({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) {
    return (
        <div className="flex flex-col">
            <span className="text-[9px] font-medium text-muted-foreground text-right mb-0.5">
                {label}
            </span>
            <div className={`border rounded px-2 py-1.5 text-right min-w-[130px] ${highlight ? 'border-primary bg-primary/5' : 'border-border bg-background'}`}>
                <span className={`text-xs font-medium ${highlight ? 'text-primary' : 'text-foreground'}`}>
                    {value}
                </span>
            </div>
        </div>
    )
}
export function ObjectViewPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { t } = useTranslation()
    // State declarations (must be before hooks that use them)
    const [jsonDialog, setJsonDialog] = useState<{ open: boolean; title: string; content: string | null }>({
        open: false,
        title: '',
        content: null
    })
    const [rtoDialogOpen, setRtoDialogOpen] = useState(false)
    const [rtoDetailsOpen, setRtoDetailsOpen] = useState(false)
    const [rpoDialogOpen, setRpoDialogOpen] = useState(false)
    const [activeTab, setActiveTab] = useState('versions')
    const [subObjectsSearch, setSubObjectsSearch] = useState('')
    const [runbookAutoUpdate, setRunbookAutoUpdate] = useState(false)
    const [changeRunbookDialogOpen, setChangeRunbookDialogOpen] = useState(false)
    const [cloneRunbookDialogOpen, setCloneRunbookDialogOpen] = useState(false)
    const [saveAsMasterDialogOpen, setSaveAsMasterDialogOpen] = useState(false)
    const [retrieveVersionDialogOpen, setRetrieveVersionDialogOpen] = useState(false)
    const [selectedVersion, setSelectedVersion] = useState<import('@/types/entities').ObjectVersion | null>(null)
    const [requestRecoveryDialogOpen, setRequestRecoveryDialogOpen] = useState(false)
    const [visFullscreen, setVisFullscreen] = useState(false)
    const [analyticsDialog, setAnalyticsDialog] = useState<{ open: boolean; analyticsText: string | null }>({ open: false, analyticsText: null })
    const [anomalyChartOpen, setAnomalyChartOpen] = useState(false)
    const [forensicsDialogOpen, setForensicsDialogOpen] = useState(false)
    // Initialize selectedScenario from localStorage, or empty string if not set
    const getInitialScenario = (): string => {
        try {
            const stored = localStorage.getItem('selectedScenario')
            return stored || ''
        } catch {
            return ''
        }
    }
    const [selectedScenario, setSelectedScenario] = useState<string>(getInitialScenario)
    // Save to localStorage whenever selectedScenario changes
    useEffect(() => {
        try {
            if (selectedScenario) {
                localStorage.setItem('selectedScenario', selectedScenario)
            } else {
                localStorage.removeItem('selectedScenario')
            }
        } catch (error) {
            console.warn('Failed to save scenario to localStorage:', error)
        }
    }, [selectedScenario])
    // Hooks - need scenarios first to calculate scenarioParam
    const { data: versions = [], isLoading: isLoadingVersions } = useObjectVersions(id || null)
    const { data: scenarios = [] } = useScenarios(id || null)
    // Find the default scenario (where Default = 1)
    const defaultScenario = scenarios.find((s) => s.isDefault)
    const defaultScenarioId = defaultScenario?.id.toString() || null
    // Use selectedScenario if set, otherwise use the default scenario
    const effectiveScenario = selectedScenario || defaultScenarioId
    const scenarioParam = effectiveScenario || undefined
    const queryClient = useQueryClient()
    // Now we can use scenarioParam in hooks
    const { data: object, isLoading, error } = useObject(id || null, scenarioParam)
    const { data: serviceTableData = [], isLoading: isLoadingServiceTable } = useServiceTable(id || null, scenarioParam)
    const { data: rtoBreakdown = [], isLoading: isLoadingRtoBreakdown } = useRolledUpRTO(id || null, scenarioParam)
    const { data: rpoBreakdown = [], isLoading: isLoadingRpoBreakdown } = useRolledUpRPO(id || null, scenarioParam)
    // Always fetch RTO details when scenario is available (not just when dialog is open) to get endToEndTime
    const { data: rtoDetails, isLoading: isLoadingRtoDetails } = useObjectRtoDetails(id || null, !!scenarioParam, scenarioParam)
    const { data: runbookTasks = [], isLoading: isLoadingRunbook } = useRunbook(id || null, scenarioParam)
    const saveRunbookMutation = useSaveRunbook()
    const { data: objectsInformation } = useObjectsInformation(id || null, scenarioParam)
    
    // Rolled Up RTO comes directly from RolledUpRTORPO.TotalCalculatedRTO filtered by scenario
    // This is fetched via the object query which now includes scenario parameter
    
    // Invalidate and refetch all scenario-dependent queries when scenario changes
    // This ensures the active tab refreshes with new data
    useEffect(() => {
        if (id && scenarioParam) {
            const decodedId = decodeURIComponent(id)
            // Invalidate all queries that depend on scenario
            queryClient.invalidateQueries({ 
                queryKey: ['objects', decodedId, scenarioParam],
                refetchType: 'active' // Only refetch active queries (visible tabs)
            })
            queryClient.invalidateQueries({ 
                queryKey: ['objects', decodedId, 'runbook', scenarioParam],
                refetchType: 'active'
            })
            queryClient.invalidateQueries({ 
                queryKey: ['objects', decodedId, 'service-table', scenarioParam],
                refetchType: 'active'
            })
            queryClient.invalidateQueries({ 
                queryKey: ['objects', decodedId, 'rolled-up-rto', scenarioParam],
                refetchType: 'active'
            })
            queryClient.invalidateQueries({ 
                queryKey: ['objects', decodedId, 'rolled-up-rpo', scenarioParam],
                refetchType: 'active'
            })
            queryClient.invalidateQueries({ 
                queryKey: ['objects', decodedId, 'rto-details', scenarioParam],
                refetchType: 'active'
            })
            queryClient.invalidateQueries({ 
                queryKey: ['objects', decodedId, 'information', scenarioParam],
                refetchType: 'active'
            })
        }
    }, [scenarioParam, id, queryClient])
    
    // Debug logging for runbook tasks (only log when tasks actually change, not on every render)
    useEffect(() => {
        if (runbookTasks.length > 0) {
            console.log('[ObjectViewPage] runbookTasks from query changed', {
                tasksCount: runbookTasks.length,
                firstTaskStartTime: runbookTasks[0]?.startTime,
                firstTaskFinishTime: runbookTasks[0]?.finishTime,
            })
        }
    }, [runbookTasks.length, runbookTasks[0]?.startTime, runbookTasks[0]?.finishTime])
    // Check if this is a Service object
    const isService = object?.objectService === 'Service'
    // Check if this is a Management Only object
    const isManagementOnly = object?.managementOnly === 1
    const handleShowJson = useCallback((title: string, content: string | null) => {
        setJsonDialog({ open: true, title, content })
    }, [])
    const handleRolledUpRTOClick = useCallback(() => {
        setRtoDialogOpen(true)
    }, [])
    const handleRolledUpRPOClick = useCallback(() => {
        setRpoDialogOpen(true)
    }, [])
    const handleEndToEndClick = useCallback(() => {
        setRtoDetailsOpen(true)
    }, [])
    const handleRetrieveVersion = useCallback((version: import('@/types/entities').ObjectVersion) => {
        setSelectedVersion(version)
        setRetrieveVersionDialogOpen(true)
    }, [])
    const handleViewRetrievalInfo = useCallback((version: import('@/types/entities').ObjectVersion) => {
        // TODO: Implement view retrieval information functionality
        console.log('View retrieval info:', version)
    }, [])
    // RunbookTable manages its own state in memory
    // Parent only needs to handle saving - no state synchronization needed
    const handleRunbookSave = useCallback(async (tasksToSave: RunbookTask[]) => {
        console.log('[ObjectViewPage] handleRunbookSave called', {
            tasksCount: tasksToSave.length,
            firstTaskStartTime: tasksToSave[0]?.startTime,
            firstTaskFinishTime: tasksToSave[0]?.finishTime,
            scenario: effectiveScenario,
        })
        if (!id) return
        const decodedId = decodeURIComponent(id)
        const scenarioToSave = effectiveScenario || undefined
        await saveRunbookMutation.mutateAsync({
            objectID: decodedId,
            tasks: tasksToSave,
            scenario: scenarioToSave,
        })
        console.log('[ObjectViewPage] Save mutation completed')
    }, [id, saveRunbookMutation, effectiveScenario])
    // Handle changing runbook from master
    const handleChangeRunbookFromMaster = useCallback(async (masterRunbookId: number, customizedTasks?: RunbookTask[]) => {
        if (!id || !scenarioParam) {
            throw new Error('Object ID and scenario are required')
        }
        try {
            const decodedId = decodeURIComponent(id)
            
            // Step 1: Change runbook from master
            await objectsApi.changeRunbookFromMaster(decodedId, masterRunbookId, scenarioParam)
            
            // Step 2: Always save tasks with calculated times (even if not customized)
            // This ensures start/finish times are saved to the database
            // The backend changeRunbookFromMaster inserts tasks with NULL times,
            // so we need to save them with calculated times
            if (customizedTasks && customizedTasks.length > 0) {
                await saveRunbookMutation.mutateAsync({
                    objectID: decodedId,
                    tasks: customizedTasks,
                    scenario: scenarioParam,
                })
            }
            
            // Invalidate and refetch runbook data + the RTO/RPO queries that the top
            // card shows — the ObjectRTO view recomputes from the newly assigned tasks,
            // so End to End / Rolled Up RTO must refresh.
            await queryClient.invalidateQueries({ queryKey: ['objects', id, 'runbook', scenarioParam] })
            queryClient.invalidateQueries({ queryKey: ['objects', decodedId, 'rto-details', scenarioParam] })
            queryClient.invalidateQueries({ queryKey: ['objects', decodedId, 'rolled-up-rto', scenarioParam] })
            queryClient.invalidateQueries({ queryKey: ['objects', decodedId, 'rolled-up-rpo', scenarioParam] })
            queryClient.invalidateQueries({ queryKey: ['objects', decodedId, scenarioParam] })
            console.log('Runbook changed from master', {
                objectID: id,
                masterRunbookId,
                scenario: scenarioParam,
                hasCustomizedTasks: !!customizedTasks
            })
        } catch (error) {
            console.error('Error changing runbook from master:', error)
            throw error
        }
    }, [id, scenarioParam, queryClient, saveRunbookMutation])

    // Handle cloning runbook from another object
    const handleCloneRunbook = useCallback(async (sourceObjectID: string, sourceScenario: string, customizedTasks?: RunbookTask[]) => {
        if (!id || !scenarioParam) {
            throw new Error('Object ID and scenario are required')
        }
        try {
            const decodedId = decodeURIComponent(id)
            const decodedSourceId = decodeURIComponent(sourceObjectID)
            
            // Clone runbook from source object
            await objectsApi.cloneRunbook(decodedId, decodedSourceId, sourceScenario, scenarioParam)
            
            // If customized tasks were provided, save them
            if (customizedTasks && customizedTasks.length > 0) {
                await saveRunbookMutation.mutateAsync({
                    objectID: decodedId,
                    tasks: customizedTasks,
                    scenario: scenarioParam,
                })
            }
            
            // Invalidate and refetch runbook data
            await queryClient.invalidateQueries({ queryKey: ['objects', id, 'runbook', scenarioParam] })
            console.log('Runbook cloned', {
                objectID: id,
                sourceObjectID,
                sourceScenario,
                scenario: scenarioParam,
            })
        } catch (error) {
            console.error('Error cloning runbook:', error)
            throw error
        }
    }, [id, scenarioParam, queryClient, saveRunbookMutation])

    // Handle saving runbook as master
    const handleSaveAsMaster = useCallback(async (name: string, description: string) => {
        if (!id || !scenarioParam) {
            throw new Error('Object ID and scenario are required')
        }
        try {
            const decodedId = decodeURIComponent(id)
            
            // Get current runbook tasks
            const response = await objectsApi.getRunbook(decodedId, scenarioParam)
            const tasks = response.data?.data || response.data || []
            
            if (tasks.length === 0) {
                throw new Error('No runbook tasks to save')
            }
            
            // Save as master runbook
            await objectsApi.saveRunbookAsMaster(decodedId, scenarioParam, name, description)
            
            console.log('Runbook saved as master', {
                objectID: id,
                scenario: scenarioParam,
                name,
            })
        } catch (error) {
            console.error('Error saving runbook as master:', error)
            throw error
        }
    }, [id, scenarioParam])

    const handleShowAnalytics = useCallback((analyticsText: string) => {
        setAnalyticsDialog({ open: true, analyticsText })
    }, [])

    const handleShowAnomalyChart = useCallback(() => {
        setAnomalyChartOpen(true)
    }, [])

    // Create columns for object versions table
    const versionColumns = useMemo(() => {
        if (!object) return []
        return createObjectVersionColumns(
            {
                bootDisk: object.bootDisk,
                fullNo: object.fullNo,
                intNo: object.intNo,
                logNo: object.logNo
            },
            t,
            handleShowJson,
            handleRetrieveVersion,
            handleViewRetrievalInfo,
            handleShowAnalytics,
            handleShowAnomalyChart
        )
    }, [object, t, handleShowJson, handleRetrieveVersion, handleViewRetrievalInfo, handleShowAnalytics, handleShowAnomalyChart])
    // Create columns for service table
    const serviceColumns = useMemo(() => {
        return createServiceTableColumns(t, handleShowJson)
    }, [t, handleShowJson])
    // Filter service table data based on search
    const filteredServiceTableData = useMemo(() => {
        if (!subObjectsSearch.trim()) {
            return serviceTableData
        }
        const searchLower = subObjectsSearch.toLowerCase().trim()
        return serviceTableData.filter((row) => {
            const sourceID = ((row as any).sourceID || (row as any).objectID || '').toLowerCase()
            const description = (row.description || '').toLowerCase()
            return sourceID.includes(searchLower) || description.includes(searchLower)
        })
    }, [serviceTableData, subObjectsSearch])
    if (isLoading) {
        return (
            <AdminRoute>
                <div className="flex items-center justify-center h-64">
                    <div className="text-muted-foreground">{t('objects.loadingObject')}</div>
                </div>
            </AdminRoute>
        )
    }
    if (error) {
        return (
            <AdminRoute>
                <div className="space-y-6">
                    <Button variant="ghost" onClick={() => navigate(-1)}>
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        {t('common.back')}
                    </Button>
                    <div className="flex items-center justify-center h-64">
                        <div className="text-red-500">
                            {t('objects.errorLoadingObject')}: {error instanceof Error ? error.message : String(error)}
                        </div>
                    </div>
                </div>
            </AdminRoute>
        )
    }
    if (!object) {
        return (
            <AdminRoute>
                <div className="space-y-6">
                    <Button variant="ghost" onClick={() => navigate(-1)}>
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        {t('common.back')}
                    </Button>
                    <div className="flex items-center justify-center h-64">
                        <div className="text-muted-foreground">{t('objects.objectNotFound')}</div>
                    </div>
                </div>
            </AdminRoute>
        )
    }
    return (
        <AdminRoute>
            <div className="space-y-4">
                {/* Back button and Scenario Dropdown - Same line */}
                <div className="flex items-center justify-between">
                    <Button variant="ghost" onClick={() => navigate(-1)}>
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        {t('common.back')}
                    </Button>
                    <ScenarioSelect
                        objectID={id || null}
                        value={effectiveScenario}
                        onValueChange={setSelectedScenario}
                        selectClassName="w-[300px] h-9 font-bold"
                    />
                </div>
                {/* Object Information - Compact */}
                <div className="-mb-10">
                    <div>
                        <Card>
                            <CardContent className="py-4 relative">
                                {/* Request Recovery & Forensics Buttons - Top Right */}
                                <div className="absolute top-4 right-4 flex flex-col gap-2 items-end">
                                    <Button
                                        variant="default"
                                        size="sm"
                                        onClick={() => setRequestRecoveryDialogOpen(true)}
                                        disabled={!effectiveScenario}
                                    >
                                        {t('objects.requestRecovery')}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setForensicsDialogOpen(true)}
                                    >
                                        <Microscope className="h-4 w-4 mr-1" />
                                        {t('objects.forensics')}
                                    </Button>
                                    {id && !isService && !isManagementOnly && (
                                        <AnomalyHistoryChart objectID={decodeURIComponent(id)} />
                                    )}
                                </div>
                                <div className="flex flex-col lg:flex-row gap-4">
                                    {/* Left Side - Object Details */}
                                    <div className="space-y-2 shrink-0">
                                        <div className="flex items-center gap-3">
                                            <Label className="text-xs font-medium text-primary w-28 shrink-0">
                                                {t('objects.objectID')}
                                            </Label>
                                            <div className="flex items-center gap-2">
                                                <p className="text-base font-bold">{object.objectID || '-'}</p>
                                                {object.iconFilename && (
                                                    <img
                                                        src={buildIconPath(object.iconFilename)}
                                                        alt=""
                                                        className="h-8 w-8 object-contain"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Label className="text-xs font-medium text-muted-foreground w-28 shrink-0">
                                                {t('objects.objectDescription')}
                                            </Label>
                                            <p className="text-sm text-foreground">{object.objectDescription ? <TranslatableText text={object.objectDescription} /> : '-'}</p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Label className="text-xs font-medium text-muted-foreground w-28 shrink-0 flex items-center gap-1">
                                                <Layers className="h-3 w-3" />
                                                {t('objects.layerID')}
                                            </Label>
                                            <p className="text-sm text-foreground">
                                                {object.layerID !== null && object.layerID !== undefined
                                                    ? <>{object.layerID}{object.layerDescription ? <> - <TranslatableText text={object.layerDescription} /></> : ''}</>
                                                    : '-'}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Label className="text-xs font-medium text-muted-foreground w-28 shrink-0 flex items-center gap-1">
                                                <MapPin className="h-3 w-3" />
                                                {t('objects.objectLocation')}
                                            </Label>
                                            <p className="text-sm text-foreground">{object.objectLocation ? <TranslatableText text={object.objectLocation} /> : '-'}</p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Label className="text-xs font-medium text-muted-foreground w-28 shrink-0">
                                                {t('objects.objectStatus')}
                                            </Label>
                                            <StatusIndicator status={object.objectStatus ? Number(object.objectStatus) : null} />
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Label className="text-xs font-medium text-muted-foreground w-28 shrink-0">
                                                {t('objects.applicationEncryption')}
                                            </Label>
                                            {object.applicationEncryption === 1 || object.applicationEncryption === '1' ? (
                                                <img
                                                    src="/images/ApplicationEncryption.png"
                                                    alt="Application Encryption"
                                                    className="h-4 w-4"
                                                />
                                            ) : (
                                                <span className="text-muted-foreground">-</span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Label className="text-xs font-medium text-muted-foreground w-28 shrink-0">
                                                {t('objects.datastore')}
                                            </Label>
                                            {object.datastore === 1 ? (
                                                <img
                                                    src="/images/database.png"
                                                    alt="Datastore"
                                                    className="h-4 w-4"
                                                />
                                            ) : (
                                                <span className="text-muted-foreground">-</span>
                                            )}
                                        </div>
                                    </div>
                                    {/* Middle - RPO and RTO Calculation (positioned at bottom) */}
                                    <div className="flex flex-col items-start justify-end gap-2 -ml-24">
                                        {/* RPO Values - aligned so Rolled Up RPO right edge matches End to End RTO right edge */}
                                        <div className="w-full flex items-end gap-2 justify-end">
                                            <TimeDisplayBox
                                                label={t('objects.rpo')}
                                                value={object.calculatedRPO !== null && object.calculatedRPO !== undefined ? formatSecondsToDHM(object.calculatedRPO) : '-'}
                                            />
                                            <TimeDisplayBox
                                                label={t('objects.realTimeRPO')}
                                                value={object.realTimeRPO !== null && object.realTimeRPO !== undefined ? formatSecondsToDHM(object.realTimeRPO) : '-'}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleRolledUpRPOClick}
                                                className="focus:outline-none"
                                                title={t('objects.rolledUpRPO')}
                                            >
                                                <TimeDisplayBox
                                                    label={t('objects.rolledUpRPO')}
                                                    value={object.rolledUpRPO !== null && object.rolledUpRPO !== undefined ? formatSecondsToDHM(object.rolledUpRPO) : '-'}
                                                    highlight
                                                />
                                            </button>
                                        </div>
                                        {/* Rolled Up RTO above End to End RTO */}
                                        <div className="w-full flex items-end gap-2 justify-end">
                                            <div className="w-[130px]"></div>
                                            <div className="w-[130px]"></div>
                                            <div className="w-[130px]"></div>
                                            <div className="w-[130px]"></div>
                                            <div className="w-4"></div>
                                            <button
                                                type="button"
                                                onClick={handleRolledUpRTOClick}
                                                className="focus:outline-none"
                                                title={t('objects.rolledUpRTO')}
                                            >
                                                <TimeDisplayBox
                                                    label={t('objects.rolledUpRTO')}
                                                    value={object.rolledUpRTO !== null && object.rolledUpRTO !== undefined ? formatSecondsToDHM(object.rolledUpRTO) : '-'}
                                                    highlight
                                                />
                                            </button>
                                        </div>
                                        {/* RTO Calculation (simplified) */}
                                        <div className="w-full flex items-end gap-2 justify-end">
                                            <TimeDisplayBox
                                                label={t('objects.objectSize')}
                                                value={object.objectSize !== null && object.objectSize !== undefined ? formatBytes(object.objectSize) : '-'}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleEndToEndClick}
                                                className="focus:outline-none"
                                                title={t('objects.endToEndRTO')}
                                            >
                                                <TimeDisplayBox
                                                    label={t('objects.endToEndRTO')}
                                                    value={rtoDetails?.endToEndTime !== null && rtoDetails?.endToEndTime !== undefined ? formatSecondsToDHM(rtoDetails.endToEndTime) : (object.endToEndTime !== null && object.endToEndTime !== undefined ? formatSecondsToDHM(object.endToEndTime) : '-')}
                                                    highlight
                                                />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
                {/* Anomaly History - button next to Request Recovery / Forensics */}
                {/* Table Tabs */}
                <div className="mt-6">
                    <Card className="overflow-visible">
                        <CardContent className="p-0 overflow-visible">
                            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                                <TabsList className="w-full justify-start border-b rounded-none h-auto p-0 bg-transparent">
                                    <TabsTrigger
                                        value="versions"
                                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2"
                                    >
                                        <FileStack className="mr-2 h-4 w-4 text-blue-600" />
                                        {t('objects.versions.title')}
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value="visualization"
                                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2"
                                    >
                                        <Network className="mr-2 h-4 w-4 text-blue-600" />
                                        {t('objects.tabs.canvas', 'Canvas')}
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value="subobjects"
                                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2"
                                    >
                                        <ListTree className="mr-2 h-4 w-4 text-blue-600" />
                                        {t('objects.rolledUpView')}
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value="runbook"
                                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2"
                                    >
                                        <List className="mr-2 h-4 w-4 text-blue-600" />
                                        {t('objects.runbook.title')}
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value="floorPlan"
                                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2"
                                    >
                                        <Map className="mr-2 h-4 w-4 text-blue-600" />
                                        {t('objects.tabs.floorPlan')}
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value="documentVideo"
                                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2"
                                    >
                                        <Video className="mr-2 h-4 w-4 text-blue-600" />
                                        {t('objects.tabs.recoveryDocumentVideo')}
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value="image"
                                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2"
                                    >
                                        <Image className="mr-2 h-4 w-4 text-blue-600" />
                                        {t('objects.tabs.objectPicture')}
                                    </TabsTrigger>
                                                                        <TabsTrigger
                                        value="datastores"
                                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2"
                                    >
                                        <Database className="mr-2 h-4 w-4 text-blue-600" />
                                        {t('objects.tabs.datastores', 'Datastores')}
                                    </TabsTrigger>
                                </TabsList>
                                {/* Object Versions Tab */}
                                <TabsContent value="versions" className="px-4 py-4">
                                    {isLoadingVersions ? (
                                        <div className="flex items-center justify-center h-32">
                                            <div className="text-muted-foreground">{t('objects.versions.loading')}</div>
                                        </div>
                                    ) : isService ? (
                                        <div className="flex items-center justify-center h-32">
                                            <div className="text-muted-foreground">{t('objects.noObjectVersions')}</div>
                                        </div>
                                    ) : isManagementOnly ? (
                                        <div className="flex items-center justify-center h-32">
                                            <div className="text-muted-foreground">{t('objects.managementOnlyMessage')}</div>
                                        </div>
                                    ) : (
                                        <div className="min-w-full">
                                            <DataTable
                                                columns={versionColumns}
                                                data={versions}
                                                className="text-sm leading-5"
                                                enableRowSelection={false}
                                                disablePagination={true}
                                            />
                                        </div>
                                    )}
                                </TabsContent>
                                {/* Visualization Tab */}
                                <TabsContent value="visualization" className={visFullscreen ? "fixed inset-0 z-50 bg-background p-0" : "p-0"}>
                                    <div className={visFullscreen ? "h-screen w-screen" : "h-[600px] w-full"} style={{ position: 'relative' }}>
                                        <button
                                            onClick={() => setVisFullscreen(!visFullscreen)}
                                            className="absolute top-2 left-2 z-[2000] bg-white border border-gray-300 rounded-md p-1.5 shadow-md hover:bg-gray-100"
                                            title={visFullscreen ? "Exit full screen" : "Full screen"}
                                        >
                                            {visFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                                        </button>
                                        {activeTab === 'visualization' && (
                                            <Suspense fallback={
                                                <div className="flex h-full items-center justify-center">
                                                    <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                                                </div>
                                            }>
                                                <AppStateProvider>
                                                    <ReactFlowProvider>
                                                        <VisualizationApp
                                                            scenario="planning"
                                                            view="dependency"
                                                            objectId={id}
                                                            showMainPalette={false}
                                                            scenarioId={scenarioParam || '1'}
                                                            serviceId={scenarioParam || '1'}
                                                        />
                                                    </ReactFlowProvider>
                                                </AppStateProvider>
                                            </Suspense>
                                        )}
                                    </div>
                                </TabsContent>
                                {/* Sub Objects / Service View Tab */}
                                <TabsContent value="subobjects" className="px-4 py-4">
                                    {isLoadingServiceTable ? (
                                        <div className="flex items-center justify-center h-32">
                                            <div className="text-muted-foreground">{t('objects.service.loading')}</div>
                                        </div>
                                    ) : (
                                        <div className="min-w-full">
                                            <DataTable
                                                columns={serviceColumns}
                                                data={filteredServiceTableData}
                                                searchKey="sourceID"
                                                searchValue={subObjectsSearch}
                                                onSearchChange={setSubObjectsSearch}
                                                searchPlaceholder={t('objects.searchPlaceholder')}
                                                className="text-sm leading-5"
                                                enableRowSelection={false}
                                                disablePagination={true}
                                            />
                                        </div>
                                    )}
                                </TabsContent>
                                {/* Runbook Tab */}
                                <TabsContent value="runbook" className="px-4 py-4">
                                    <div className="space-y-4">
                                        {isLoadingRunbook ? (
                                            <div className="flex items-center justify-center h-32">
                                                <div className="text-muted-foreground">{t('objects.service.loading')}</div>
                                            </div>
                                        ) : (
                                            <RunbookTable
                                                key={`runbook-${id}-${scenarioParam}`}
                                                tasks={runbookTasks || []}
                                                onSave={handleRunbookSave}
                                                autoUpdate={runbookAutoUpdate}
                                                onAutoUpdateChange={setRunbookAutoUpdate}
                                                isSaving={saveRunbookMutation.isPending}
                                                onReplaceWithMaster={() => setChangeRunbookDialogOpen(true)}
                                                onCloneRunbook={() => setCloneRunbookDialogOpen(true)}
                                                onSaveAsMaster={() => setSaveAsMasterDialogOpen(true)}
                                            />
                                        )}
                                    </div>
                                </TabsContent>
                                {/* Floor Plan Tab */}
                                <TabsContent value="floorPlan" className="px-4 py-4">
                                    <FloorPlanViewTab
                                        objectID={object?.objectID}
                                        locationID={object?.locationID}
                                        floorPlanX={object?.floorPlanX}
                                        floorPlanY={object?.floorPlanY}
                                    />
                                </TabsContent>
                                {/* Document and Video Tab */}
                                <TabsContent value="documentVideo" className="px-4 py-4">
                                    <RecoveryDocumentVideoViewTab
                                        objectID={id ? decodeURIComponent(id) : undefined}
                                        scenario={scenarioParam}
                                    />
                                </TabsContent>
                                {/* Image Tab */}
                                <TabsContent value="image" className="px-4 py-4">
                                    <ObjectImageViewTab image={objectsInformation?.image} />
                                </TabsContent>
                                {/* Datastores Tab */}
                                                                <TabsContent value="datastores" className="px-4 py-4">
                                    <ReconciliationChart
                                        mainId={id || ''}
                                        scenario={effectiveScenario ? parseInt(effectiveScenario, 10) : null}
                                    />
                                </TabsContent>
                            </Tabs>
                        </CardContent>
                    </Card>
                </div>
            </div>
            {/* JSON Viewer Dialog */}
            <JsonViewDialog
                open={jsonDialog.open}
                onOpenChange={(open) => setJsonDialog(prev => ({ ...prev, open }))}
                title={jsonDialog.title}
                content={jsonDialog.content}
            />
            {/* Analytics Report Dialog */}
            <AnalyticsReportDialog
                open={analyticsDialog.open}
                onOpenChange={(open) => setAnalyticsDialog(prev => ({ ...prev, open }))}
                analyticsText={analyticsDialog.analyticsText}
            />
            {/* Anomaly Size Chart Dialog */}
            <AnomalySizeChartDialog
                open={anomalyChartOpen}
                onOpenChange={setAnomalyChartOpen}
                objectID={id ? decodeURIComponent(id) : ''}
            />
            {/* End-to-End RTO Details Dialog */}
            <Dialog open={rtoDetailsOpen} onOpenChange={setRtoDetailsOpen}>
                <DialogContent className="max-w-lg w-full max-h-[70vh]">
                    <DialogHeader>
                        <DialogTitle>{t('objects.endToEndRTO')}</DialogTitle>
                        <DialogDescription>{t('objects.endToEndRTO')}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="border rounded-lg overflow-hidden">
                            <div className="grid grid-cols-4 bg-muted/60 text-xs font-semibold text-muted-foreground px-3 py-2">
                                <span>Egress</span>
                                <span>Link</span>
                                <span>Engineer</span>
                                <span className="text-right">End to End</span>
                            </div>
                            <div className="divide-y divide-border">
                                {isLoadingRtoDetails ? (
                                    <div className="p-3 text-sm text-muted-foreground">{t('objects.loadingObject')}</div>
                                ) : (
                                    <div className="grid grid-cols-4 px-3 py-2 text-sm">
                                        <span>{rtoDetails?.egressTime !== null && rtoDetails?.egressTime !== undefined ? formatSecondsToDHM(rtoDetails.egressTime) : '-'}</span>
                                        <span>{rtoDetails?.linkTime !== null && rtoDetails?.linkTime !== undefined ? formatSecondsToDHM(rtoDetails.linkTime) : '-'}</span>
                                        <span>{rtoDetails?.engineerRTO !== null && rtoDetails?.engineerRTO !== undefined ? formatSecondsToDHM(rtoDetails.engineerRTO) : '-'}</span>
                                        <span className="text-right">{rtoDetails?.endToEndTime !== null && rtoDetails?.endToEndTime !== undefined ? formatSecondsToDHM(rtoDetails.endToEndTime) : '-'}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            {/* Rolled Up RTO Breakdown Dialog */}
            <Dialog open={rtoDialogOpen} onOpenChange={setRtoDialogOpen}>
                <DialogContent className="max-w-4xl w-full max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle>{t('objects.rolledUpRTO')}, {object?.objectID || id || ''}</DialogTitle>
                        <DialogDescription>{t('objects.rolledUpRTO')}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="flex justify-end pr-3">
                            <div className="text-sm text-muted-foreground flex items-center gap-1">
                                <span>{t('objects.rolledUpRTO')}:</span>
                                <span className="font-semibold text-foreground">
                                    {object?.rolledUpRTO !== null && object?.rolledUpRTO !== undefined ? formatSecondsToDHM(object.rolledUpRTO) : '-'}
                                </span>
                            </div>
                        </div>
                        <div className="border rounded-lg overflow-hidden">
                            <div className="grid grid-cols-[150px_1fr_80px_100px] bg-muted/60 text-xs font-semibold text-muted-foreground px-3 py-2 gap-3">
                                <span>Object ID</span>
                                <span>Description</span>
                                <span>Source Layer</span>
                                <span className="text-right">RTO</span>
                            </div>
                            <div className="max-h-64 overflow-auto divide-y divide-border">
                                {isLoadingRtoBreakdown ? (
                                    <div className="p-3 text-sm text-muted-foreground">{t('objects.loadingObject')}</div>
                                ) : rtoBreakdown.length === 0 ? (
                                    <div className="p-3 text-sm text-muted-foreground">No data</div>
                                ) : (
                                    rtoBreakdown.map((row, idx) => (
                                        <div key={`${row.objectID}-${row.layerID}-${idx}`} className="grid grid-cols-[150px_1fr_80px_100px] px-3 py-2 text-sm gap-3">
                                            <span className="truncate" title={row.objectID}>{row.objectID}</span>
                                            <span className="truncate" title={row.objectDescription}>{row.objectDescription ? <TranslatableText text={row.objectDescription} /> : '-'}</span>
                                            <span>{row.layerID ?? '-'}</span>
                                            <span className="text-right">{row.rto !== null && row.rto !== undefined ? formatSecondsToDHM(row.rto) : '-'}</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            {/* Rolled Up RPO Breakdown Dialog */}
            <Dialog open={rpoDialogOpen} onOpenChange={setRpoDialogOpen}>
                <DialogContent className="max-w-4xl w-full max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle>{t('objects.rolledUpRPO')} {object?.objectID || id || ''}</DialogTitle>
                        <DialogDescription>{t('objects.rolledUpRPO')}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-2">
                            <div className="flex justify-end pr-3">
                                <div className="text-sm text-muted-foreground flex items-center gap-1">
                                    <span>{t('objects.rolledUpRPO')}:</span>
                                    <span className="font-semibold text-foreground">
                                        {object?.rolledUpRPO !== null && object?.rolledUpRPO !== undefined ? formatSecondsToDHM(object.rolledUpRPO) : '-'}
                                    </span>
                                </div>
                            </div>
                            <div className="border rounded-lg overflow-hidden">
                                <div className="grid grid-cols-[150px_1fr_100px_100px] bg-muted/60 text-xs font-semibold text-muted-foreground px-3 py-2 gap-3">
                                    <span>Object ID</span>
                                    <span>Description</span>
                                    <span className="text-center">Source Layer</span>
                                    <span className="text-right">RPO</span>
                                </div>
                                <div className="max-h-64 overflow-auto divide-y divide-border">
                                    {isLoadingRpoBreakdown ? (
                                        <div className="p-3 text-sm text-muted-foreground">{t('objects.loadingObject')}</div>
                                    ) : rpoBreakdown.length === 0 ? (
                                        <div className="p-3 text-sm text-muted-foreground">No data</div>
                                    ) : (
                                        rpoBreakdown.map((row, idx) => (
                                            <div key={`${row.objectID}-${idx}`} className="grid grid-cols-[150px_1fr_100px_100px] px-3 py-2 text-sm gap-3">
                                                <span className="truncate" title={row.objectID}>{row.objectID}</span>
                                                <span className="truncate" title={row.objectDescription}>{row.objectDescription ? <TranslatableText text={row.objectDescription} /> : '-'}</span>
                                                <span className="text-center">{row.sourceLayer ?? '-'}</span>
                                                <span className="text-right">{row.rpo !== null && row.rpo !== undefined ? formatSecondsToDHM(row.rpo) : '-'}</span>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
                </Dialog>
                {/* Change Runbook Dialog */}
                {id && scenarioParam && (
                    <>
                        <ChangeRunbookDialog
                            open={changeRunbookDialogOpen}
                            onOpenChange={setChangeRunbookDialogOpen}
                            objectID={id}
                            scenario={scenarioParam}
                            onApply={handleChangeRunbookFromMaster}
                        />
                        <CloneRunbookDialog
                            open={cloneRunbookDialogOpen}
                            onOpenChange={setCloneRunbookDialogOpen}
                            objectID={id}
                            scenario={scenarioParam}
                            onApply={handleCloneRunbook}
                        />
                        <SaveAsMasterDialog
                            open={saveAsMasterDialogOpen}
                            onOpenChange={setSaveAsMasterDialogOpen}
                            objectID={id}
                            scenario={scenarioParam}
                            onSave={handleSaveAsMaster}
                        />
                        <RetrieveVersionDialog
                            open={retrieveVersionDialogOpen}
                            onOpenChange={setRetrieveVersionDialogOpen}
                            version={selectedVersion}
                            objectID={id}
                        />
                        <RequestRecoveryDialog
                            open={requestRecoveryDialogOpen}
                            onOpenChange={setRequestRecoveryDialogOpen}
                            objectID={id}
                            scenario={scenarioParam ? Number(scenarioParam) : null}
                        />
                        <StartForensicsDialog
                            open={forensicsDialogOpen}
                            onOpenChange={setForensicsDialogOpen}
                            objectId={id || ''}
                            objectVersionId={versions.length > 0 ? versions[0].versionID : null}
                            objectDescription={object?.objectDescription}
                            vaultId={versions.length > 0 ? versions[0].vaultID : null}
                        />
                    </>
                )}
        </AdminRoute>
    )
}
